import React, { useState, useEffect, useCallback } from 'react';
import { Users, Download, Filter, TrendingUp, UserMinus, Briefcase, AlertTriangle, History, X } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

const ReportingRH = () => {
  const { filialeId, isConsolidated } = useFiliale();
  const [activeTab, setActiveTab] = useState('overview');
  const [overviewData, setOverviewData] = useState(null);
  const [personnelData, setPersonnelData] = useState([]);
  const [soldeCongeData, setSoldeCongeData] = useState([]);
  const [selectedEmployeeHistory, setSelectedEmployeeHistory] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [loading, setLoading] = useState(true);

  // Couleurs pour les graphiques
  const COLORS = ['#2563eb', '#ec4899', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];

  // États pour les filtres
  const [filters, setFilters] = useState({
    departement_id: '',
    business_unit_id: '',
    grade_id: '',
    marque_id: '',
    manager_id: '',
    client_id: '',
    campagne_id: '',
    activite_id: '',
    type_contrat_id: '',
    anciennete: ''
  });

  // États pour les données des dropdowns
  const [departements, setDepartements] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [grades, setGrades] = useState([]);
  const [marques, setMarques] = useState([]);
  const [managers, setManagers] = useState([]);
  const [clients, setClients] = useState([]);
  const [campagnes, setCampagnes] = useState([]);
  const [activites, setActivites] = useState([]);
  const [typesContrat, setTypesContrat] = useState([]);

  const fetchOverviewData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (!isConsolidated && filialeId) {
        params.filiale_id = filialeId;
      }

      const response = await http.get('/api/reporting/overview', { params });
      setOverviewData(response.data);
    } catch (error) {
      console.error('Erreur overview:', error);
      setOverviewData(null);
    } finally {
      setLoading(false);
    }
  }, [filialeId, isConsolidated]);

  const fetchPersonnelData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};

      if (!isConsolidated && filialeId) {
        params.filiale_id = filialeId;
      }

      if (filters.departement_id) params.departement_id = filters.departement_id;
      if (filters.business_unit_id) params.business_unit_id = filters.business_unit_id;
      if (filters.grade_id) params.grade_id = filters.grade_id;
      if (filters.marque_id) params.marque_id = filters.marque_id;
      if (filters.manager_id) params.manager_id = filters.manager_id;
      if (filters.client_id) params.client_id = filters.client_id;
      if (filters.campagne_id) params.campagne_id = filters.campagne_id;
      if (filters.activite_id) params.activite_id = filters.activite_id;
      if (filters.type_contrat_id) params.type_contrat_id = filters.type_contrat_id;
      if (filters.anciennete) params.anciennete = filters.anciennete;

      const response = await http.get('/api/reporting/personnel-register', { params });
      setPersonnelData(response.data || []);
    } catch (error) {
      console.error('Erreur registre:', error);
      setPersonnelData([]);
    } finally {
      setLoading(false);
    }
  }, [filialeId, isConsolidated, filters]);

  const fetchSoldeCongeData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (!isConsolidated && filialeId) {
        params.filiale_id = filialeId;
      }

      const response = await http.get('/api/reporting/solde-conge', { params });
      setSoldeCongeData(response.data || []);
    } catch (error) {
      console.error('Erreur solde congé:', error);
      setSoldeCongeData([]);
    } finally {
      setLoading(false);
    }
  }, [filialeId, isConsolidated]);

  const fetchEmployeeHistory = async (personnelId) => {
    try {
      const response = await http.get(`/api/conges/personnel/${personnelId}`);
      setSelectedEmployeeHistory(response.data || []);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Erreur historique:', error);
      alert('Erreur lors de la récupération de l\'historique');
    }
  };

  const fetchFilterData = async () => {
    try {
      const [departementsRes, businessUnitsRes, gradesRes, marquesRes, managersRes, clientsRes, activitesRes, typesContratRes] = await Promise.all([
        http.get('/api/departements').catch(() => ({ data: [] })),
        http.get('/api/business-units').catch(() => ({ data: [] })),
        http.get('/api/grades').catch(() => ({ data: [] })),
        http.get('/api/marques').catch(() => ({ data: [] })),
        http.get('/api/personnel/managers').catch(() => ({ data: [] })),
        http.get('/api/clients').catch(() => ({ data: [] })),
        http.get('/api/activites').catch(() => ({ data: [] })),
        http.get('/api/types-contrat').catch(() => ({ data: [] }))
      ]);

      setDepartements(Array.isArray(departementsRes.data) ? departementsRes.data.map(d => ({ id: d.iddepartement, nom: d.libelle_departement })) : []);
      setMarques(Array.isArray(marquesRes.data) ? marquesRes.data.map(m => ({ id: m.id, nom: m.libelle })) : []);
      setBusinessUnits(Array.isArray(businessUnitsRes.data) ? businessUnitsRes.data.map(bu => ({ id: bu.idbusiness_unit, nom: bu.libelle })) : []);
      setGrades(Array.isArray(gradesRes.data) ? gradesRes.data.map(g => ({ id: g.idgrade, nom: g.libelle })) : []);
      setManagers(Array.isArray(managersRes.data) ? managersRes.data.map(m => ({ id: m.idpersonnel, nom: m.nom, prenom: m.prenom })) : []);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data.map(c => ({ id: c.idclient, nom: c.Libelle_client })) : []);
      setActivites(Array.isArray(activitesRes.data) ? activitesRes.data.map(a => ({ id: a.idactivites, nom: a.Libelle_activite })) : []);
      setTypesContrat(Array.isArray(typesContratRes.data) ? typesContratRes.data.map(tc => ({ id: tc.idtype_contrat, nom: tc.nom })) : []);
    } catch (error) {
      console.error('Erreur chargement filtres:', error);
      setDepartements([]);
      setBusinessUnits([]);
      setGrades([]);
      setMarques([]);
      setManagers([]);
      setClients([]);
      setActivites([]);
      setTypesContrat([]);
    }
  };

  const fetchCampagnes = async (clientId) => {
    if (!clientId) {
      setCampagnes([]);
      return;
    }
    try {
      const response = await http.get(`/api/campagnes?client_id=${clientId}`);
      setCampagnes(Array.isArray(response.data) ? response.data.map(c => ({ id: c.idcampagne, nom: c.Libelle_campagne })) : []);
    } catch (error) {
      console.error('Erreur chargement campagnes:', error);
      setCampagnes([]);
    }
  };

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchOverviewData();
    } else if (activeTab === 'register') {
      fetchPersonnelData();
      fetchFilterData();
    } else if (activeTab === 'solde-conge') {
      fetchSoldeCongeData();
    }
  }, [activeTab, fetchOverviewData, fetchPersonnelData, fetchSoldeCongeData]);

  useEffect(() => {
    if (filters.client_id) {
      fetchCampagnes(filters.client_id);
    } else {
      setCampagnes([]);
      setFilters(prev => ({ ...prev, campagne_id: '' }));
    }
  }, [filters.client_id]);

  const handleExportPersonnel = async () => {
    try {
      const params = {};

      if (filters.departement_id) params.departement_id = filters.departement_id;
      if (filters.business_unit_id) params.business_unit_id = filters.business_unit_id;
      if (filters.grade_id) params.grade_id = filters.grade_id;
      if (filters.marque_id) params.marque_id = filters.marque_id;
      if (filters.manager_id) params.manager_id = filters.manager_id;
      if (filters.client_id) params.client_id = filters.client_id;
      if (filters.campagne_id) params.campagne_id = filters.campagne_id;
      if (filters.activite_id) params.activite_id = filters.activite_id;
      if (filters.type_contrat_id) params.type_contrat_id = filters.type_contrat_id;
      if (filters.anciennete) params.anciennete = filters.anciennete;

      if (!isConsolidated && filialeId) {
        params.filiale_id = filialeId;
      }

      const response = await http.get('/api/reporting/personnel-register/export', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `registre-personnel-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur export:', error);
    }
  };

  const handleExportSoldeConge = async () => {
    try {
      const params = {};
      if (!isConsolidated && filialeId) {
        params.filiale_id = filialeId;
      }

      const response = await http.get('/api/reporting/solde-conge/export', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `solde-conge-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur export solde congé:', error);
      alert('Erreur lors de l\'export du solde de congé');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Reporting RH"
        subtitle="Centre de pilotage et d'analyse des ressources humaines"
      />

      <div className="px-6 py-8 space-y-6">
      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-1 font-medium text-sm transition-colors ${
              activeTab === 'overview'
                ? 'text-sky-600 border-b-2 border-sky-600'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Vue d'ensemble
          </button>
          <button
            onClick={() => setActiveTab('register')}
            className={`pb-3 px-1 font-medium text-sm transition-colors ${
              activeTab === 'register'
                ? 'text-sky-600 border-b-2 border-sky-600'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Registre du personnel
          </button>
          <button
            onClick={() => setActiveTab('solde-conge')}
            className={`pb-3 px-1 font-medium text-sm transition-colors ${
              activeTab === 'solde-conge'
                ? 'text-sky-600 border-b-2 border-sky-600'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            Solde de congé
          </button>
        </nav>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-slate-500">Chargement...</div>
        </div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 relative overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Effectif Total</p>
                      <p className="text-3xl font-bold text-slate-900 mt-2">{overviewData?.total_personnel || 0}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs">
                        
                      </div>
                    </div>
                    <div className="h-12 w-12 rounded-lg bg-sky-50 flex items-center justify-center">
                      <Users className="h-6 w-6 text-sky-600" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-sky-600"></div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 relative overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Taux de Turnover</p>
                      <p className="text-3xl font-bold text-slate-900 mt-2">{overviewData?.taux_turnover || '7,4'}%</p>
                      <div className="flex items-center gap-2 mt-2 text-xs">
                        
                      </div>
                    </div>
                    <div className="h-12 w-12 rounded-lg bg-orange-50 flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-orange-600" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-orange-600"></div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 relative overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Absentéisme</p>
                      <p className="text-3xl font-bold text-slate-900 mt-2">{overviewData?.taux_absenteisme || '3,1'}%</p>
                      <div className="flex items-center gap-2 mt-2 text-xs">
                        
                      </div>
                    </div>
                    <div className="h-12 w-12 rounded-lg bg-purple-50 flex items-center justify-center">
                      <UserMinus className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-purple-600"></div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-200 relative overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Postes à pourvoir</p>
                      <p className="text-3xl font-bold text-slate-900 mt-2">{overviewData?.postes_pourvoir || 0}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs">
                        
                      </div>
                    </div>
                    <div className="h-12 w-12 rounded-lg bg-green-50 flex items-center justify-center">
                      <Briefcase className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-600"></div>
                </div>
              </div>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Répartition par type de contrat - Bar Chart */}
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">Répartition par type de contrat</h3>
                      <p className="text-sm text-slate-500 mt-1">Structure des effectifs par statut</p>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={overviewData?.contrats_par_type || []}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="type" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#2563eb" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Répartition par sexe - Donut Chart */}
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">Répartition par sexe</h3>
                      <p className="text-sm text-slate-500 mt-1">Distribution hommes/femmes</p>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={overviewData?.repartition_sexe?.map(item => ({
                          ...item,
                          name: item.sexe === 'M' ? 'Hommes' : item.sexe === 'F' ? 'Femmes' : item.sexe || 'Non spécifié'
                        })) || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="count"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {overviewData?.repartition_sexe?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.sexe === 'M' ? '#2563eb' : entry.sexe === 'F' ? '#ec4899' : '#9ca3af'} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Pyramide des âges - Bar Chart */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800">Pyramide des âges</h3>
                    <p className="text-sm text-slate-500 mt-1">Distribution par tranche d'âge</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={overviewData?.pyramide_ages || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis dataKey="tranche_age" type="category" tick={{ fontSize: 12 }} width={60} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#0ea5e9" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Répartition par département - Area Chart */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800">Répartition par département</h3>
                    <p className="text-sm text-slate-500 mt-1">Effectifs par service</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={overviewData?.departements || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="nom" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="count" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Nouveaux graphiques */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Répartition par nationalité - Pie Chart */}
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">Répartition par nationalité</h3>
                      <p className="text-sm text-slate-500 mt-1">Diversité culturelle</p>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={overviewData?.repartition_nationalite?.map(item => ({
                          ...item,
                          name: item.nationalite || 'Non spécifié'
                        })) || []}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="count"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {overviewData?.repartition_nationalite?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Répartition par niveau d'étude - Bar Chart */}
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">Répartition par niveau d'étude</h3>
                      <p className="text-sm text-slate-500 mt-1">Formation du personnel</p>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={overviewData?.repartition_niveau_etude || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="niveau_etude" type="category" tick={{ fontSize: 11 }} width={120} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8b5cf6" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Ancienneté moyenne par département - Bar Chart */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800">Ancienneté moyenne par département</h3>
                    <p className="text-sm text-slate-500 mt-1">Années d'expérience moyenne</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={overviewData?.anciennete_par_departement?.map(d => ({ ...d, anciennete_moyenne: Number(d.anciennete_moyenne).toFixed(1) })) || []}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="departement" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => [`${value} ans`, 'Ancienneté moyenne']} />
                    <Bar dataKey="anciennete_moyenne" fill="#f59e0b" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Solde de congé moyen par département - Bar Chart */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800">Solde de congé moyen par département</h3>
                    <p className="text-sm text-slate-500 mt-1">Jours de congé disponibles</p>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={overviewData?.solde_conge_par_departement?.map(d => ({ ...d, solde_moyen: Number(d.solde_moyen).toFixed(1) })) || []}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="departement" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => [`${value} jours`, 'Solde moyen']} />
                    <Bar dataKey="solde_moyen" fill="#06b6d4" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Alertes RH */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800">Alertes RH</h3>
                    <p className="text-sm text-slate-500 mt-1">Points nécessitant une attention</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex gap-3 p-3 bg-orange-50 rounded-lg">
                    <div className="h-8 w-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{overviewData?.alertes_contrats || 0} contrats arrivent à échéance</p>
                      <p className="text-xs text-slate-500 mt-1">Dans les 30 prochains jours</p>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 bg-red-50 rounded-lg">
                    <div className="h-8 w-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Taux de turnover: {overviewData?.taux_turnover || '0'}%</p>
                      <p className="text-xs text-slate-500 mt-1">Indicateur à surveiller</p>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 bg-yellow-50 rounded-lg">
                    <div className="h-8 w-8 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                      <Briefcase className="h-4 w-4 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{overviewData?.postes_pourvoir || 0} postes ouverts</p>
                      <p className="text-xs text-slate-500 mt-1">Besoin de recrutement actif</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'register' && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-800">Générer le registre du personnel</h3>
                <p className="text-sm text-slate-500 mt-1">Sélectionnez les filtres pour générer et télécharger le registre</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Département */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Département</label>
                  <select
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm"
                    value={filters.departement_id}
                    onChange={(e) => setFilters({ ...filters, departement_id: e.target.value })}
                  >
                    <option value="">Tous les départements</option>
                    {departements.map((d, index) => (
                      <option key={`departement-${d.id}-${index}`} value={d.id}>{d.nom}</option>
                    ))}
                  </select>
                </div>

                {/* Business Unit */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Business Unit</label>
                  <select
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm"
                    value={filters.business_unit_id}
                    onChange={(e) => setFilters({ ...filters, business_unit_id: e.target.value })}
                  >
                    <option value="">Toutes les Business Units</option>
                    {businessUnits.map((bu, index) => (
                      <option key={`bu-${bu.id}-${index}`} value={bu.id}>{bu.nom}</option>
                    ))}
                  </select>
                </div>

                {/* Grade */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Grade</label>
                  <select
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm"
                    value={filters.grade_id}
                    onChange={(e) => setFilters({ ...filters, grade_id: e.target.value })}
                  >
                    <option value="">Tous les grades</option>
                    {grades.map((g, index) => (
                      <option key={`grade-${g.id}-${index}`} value={g.id}>{g.nom}</option>
                    ))}
                  </select>
                </div>

                {/* Marque */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Marque</label>
                  <select
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm"
                    value={filters.marque_id}
                    onChange={(e) => setFilters({ ...filters, marque_id: e.target.value })}
                  >
                    <option value="">Toutes les marques</option>
                    {marques.map((m, index) => (
                      <option key={`marque-${m.id}-${index}`} value={m.id}>{m.nom}</option>
                    ))}
                  </select>
                </div>

                {/* Manager */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Manager</label>
                  <select
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm"
                    value={filters.manager_id}
                    onChange={(e) => setFilters({ ...filters, manager_id: e.target.value })}
                  >
                    <option value="">Tous les managers</option>
                    {managers.map((m, index) => (
                      <option key={`manager-${m.id}-${index}`} value={m.id}>{m.nom} {m.prenom}</option>
                    ))}
                  </select>
                </div>

                {/* Client */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Client</label>
                  <select
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm"
                    value={filters.client_id}
                    onChange={(e) => setFilters({ ...filters, client_id: e.target.value, campagne_id: '' })}
                  >
                    <option value="">Tous les clients</option>
                    {clients.map((c, index) => (
                      <option key={`client-${c.id}-${index}`} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>

                {/* Campagne */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Campagne</label>
                  <select
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm"
                    value={filters.campagne_id}
                    onChange={(e) => setFilters({ ...filters, campagne_id: e.target.value })}
                    disabled={!filters.client_id}
                  >
                    <option value="">Toutes les campagnes</option>
                    {campagnes.map((c, index) => (
                      <option key={`campagne-${c.id}-${index}`} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>

                {/* Activité */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Activité</label>
                  <select
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm"
                    value={filters.activite_id}
                    onChange={(e) => setFilters({ ...filters, activite_id: e.target.value })}
                  >
                    <option value="">Toutes les activités</option>
                    {activites.map((a, index) => (
                      <option key={`activite-${a.id}-${index}`} value={a.id}>{a.nom}</option>
                    ))}
                  </select>
                </div>

                {/* Type de contrat */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Type de contrat</label>
                  <select
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm"
                    value={filters.type_contrat_id}
                    onChange={(e) => setFilters({ ...filters, type_contrat_id: e.target.value })}
                  >
                    <option value="">Tous les types</option>
                    {typesContrat.map((tc, index) => (
                      <option key={`typecontrat-${tc.id}-${index}`} value={tc.id}>{tc.nom}</option>
                    ))}
                  </select>
                </div>

                {/* Ancienneté */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Ancienneté</label>
                  <select
                    className="w-full h-10 border border-gray-300 rounded-lg px-3 text-sm"
                    value={filters.anciennete}
                    onChange={(e) => setFilters({ ...filters, anciennete: e.target.value })}
                  >
                    <option value="">Toutes les anciennetés</option>
                    <option value="0-1">0 - 1 an</option>
                    <option value="1-3">1 - 3 ans</option>
                    <option value="3-5">3 - 5 ans</option>
                    <option value="5-10">5 - 10 ans</option>
                    <option value="10+">Plus de 10 ans</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mb-4">
                <button
                  onClick={fetchPersonnelData}
                  className="flex items-center gap-2 px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Filter className="h-4 w-4" />
                  Appliquer les filtres
                </button>
              </div>

              {/* Tableau des résultats */}
              {personnelData.length > 0 && (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Matricule</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Nom</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Prénoms</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Sexe</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Date de naissance</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Nationalite</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Filiale</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Marque</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Business Unit</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Département</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Client</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Campagne</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Activité</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Fonction</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Niveau</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Manager hiérarchique</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Fonction du Manager</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Statut</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Type de contrat</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Date d'intégration Groupe</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Date du premier CDD</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Date début contrat en cours</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Date fin contrat en cours</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Ancienneté Globale</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Ancienneté Contrat</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Date début période d'essai</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Date fin période d'essai</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Date de sortie</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Motif de sortie</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {personnelData.map((p, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-900">{p.matricule || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-900">{p.nom || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-900">{p.prenom || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.sexe || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.date_naissance || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.nationalite || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.filiale || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.marque || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.business_unit || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.departement || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.client || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.campagne || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.activite || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.fonction || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.niveau || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.manager_hierarchique || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.fonction_manager || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.statut || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.type_contrat || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.date_integration_groupe || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.date_premier_cdd || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.date_debut_contrat_cours || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.date_fin_contrat_cours || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.anciennete_globale || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.anciennete_contrat || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.date_debut_essai || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.date_fin_essai || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.date_sortie || ''}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{p.motif_sortie || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setFilters({
                    filiale_id: '',
                    departement_id: '',
                    business_unit_id: '',
                    grade_id: '',
                    marque_id: '',
                    manager_id: '',
                    client_id: '',
                    campagne_id: '',
                    activite_id: '',
                    type_contrat_id: '',
                    anciennete: ''
                  })}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-gray-50 transition-colors"
                >
                  Réinitialiser
                </button>
                <button
                  onClick={handleExportPersonnel}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Générer et télécharger
                </button>
              </div>
            </div>
          )}

          {activeTab === 'solde-conge' && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="mb-6 flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Solde de congé des employés</h3>
                  <p className="text-sm text-slate-500 mt-1">Consultez et modifiez les soldes de congé (Année {new Date().getFullYear()})</p>
                </div>
                <button
                  onClick={handleExportSoldeConge}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Exporter Excel
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Nom et Prénom</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Fonction</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Congés pris (année {new Date().getFullYear()})</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Solde actuel</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {soldeCongeData.map((employee) => (
                      <tr key={employee.idpersonnel} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                          {employee.nom} {employee.prenom}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {employee.titre || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {employee.conges_pris_annee || 0} jours
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <span className={employee.solde_conge < 0 ? 'text-red-600' : 'text-green-600'}>
                            {employee.solde_conge || 0} jours
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => fetchEmployeeHistory(employee.idpersonnel)}
                            className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                            title="Historique des congés"
                          >
                            <History className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {soldeCongeData.length === 0 && (
                  <div className="text-center py-8 text-slate-500">Aucun employé trouvé</div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal Historique */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">Historique des congés</h2>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date demande</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date début</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date fin</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Jours</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">État</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {selectedEmployeeHistory && selectedEmployeeHistory.length > 0 ? (
                    selectedEmployeeHistory.map((h) => (
                      <tr key={h.idplanning_conge} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                          {new Date(h.date_demande).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {new Date(h.date_debut).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {new Date(h.date_fin).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                          {h.nombre_jour}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {h.etat === 0 ? <span className="text-yellow-600">En attente</span> : 
                           h.etat === 1 ? <span className="text-green-600">Validé</span> : 
                           h.etat === 2 ? <span className="text-red-600">Rejeté</span> : 
                           h.etat === 3 ? <span className="text-gray-600">Annulé</span> : h.etat}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-slate-500">Aucun historique disponible</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ReportingRH;
